import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AvailabilityStatus = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Asia/Colombo'
    });
  };

  const isWorkingHours = () => {
    const hour = currentTime.getHours();
    return hour >= 9 && hour < 18; // 9 AM to 6 PM IST
  };

  const upcomingProjects = [
    {
      id: 1,
      title: "E-commerce Platform Redesign",
      client: "RetailTech Solutions",
      status: "In Progress",
      completion: 75,
      deadline: "Dec 15, 2024"
    },
    {
      id: 2,
      title: "Mobile App Development",
      client: "HealthCare Innovations",
      status: "Starting Soon",
      completion: 0,
      deadline: "Jan 30, 2025"
    }
  ];

  const availabilityCalendar = [
    { month: "December 2024", status: "Partially Available", slots: 2 },
    { month: "January 2025", status: "Available", slots: 4 },
    { month: "February 2025", status: "Available", slots: 4 },
    { month: "March 2025", status: "Available", slots: 4 }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Current Availability &
            <span className="gradient-text block">Project Status</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Transparent updates on my current workload and availability for new projects
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Real-time Status */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
              <div className="text-center mb-8">
                <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full border mb-4 ${
                  isWorkingHours() 
                    ? 'bg-success/10 text-success border-success/20' :'bg-warning/10 text-warning border-warning/20'
                }`}>
                  <div className={`w-2 h-2 rounded-full animate-pulse ${
                    isWorkingHours() ? 'bg-success' : 'bg-warning'
                  }`}></div>
                  <span className="text-sm font-medium">
                    {isWorkingHours() ? 'Online Now' : 'Outside Work Hours'}
                  </span>
                </div>
                
                <div className="text-2xl font-bold text-text-primary mb-2">
                  {formatTime(currentTime)}
                </div>
                <p className="text-sm text-text-muted">Colombo, Sri Lanka (GMT +5:30)</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Calendar" size={20} className="text-primary" />
                    <span className="text-sm font-medium text-text-primary">Next Available</span>
                  </div>
                  <span className="text-sm text-success font-medium">Jan 15, 2025</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Clock" size={20} className="text-accent" />
                    <span className="text-sm font-medium text-text-primary">Response Time</span>
                  </div>
                  <span className="text-sm text-accent font-medium">&lt; 24 hours</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Users" size={20} className="text-brand-purple" />
                    <span className="text-sm font-medium text-text-primary">Current Load</span>
                  </div>
                  <span className="text-sm text-brand-purple font-medium">2 Projects</span>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <Button
                  variant="default"
                  fullWidth
                  iconName="MessageSquare"
                  iconPosition="left"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Check Availability
                </Button>
              </div>
            </div>
          </div>

          {/* Current Projects */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-text-primary">Current Projects</h3>
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full">
                  <Icon name="Activity" size={16} />
                  <span className="text-sm font-medium">Active</span>
                </div>
              </div>

              <div className="space-y-6">
                {upcomingProjects.map((project) => (
                  <div key={project.id} className="border border-border rounded-xl p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-text-primary mb-1">{project.title}</h4>
                        <p className="text-sm text-text-secondary">{project.client}</p>
                      </div>
                      <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                        project.status === 'In Progress' ?'bg-warning/10 text-warning' :'bg-accent/10 text-accent'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          project.status === 'In Progress' ? 'bg-warning' : 'bg-accent'
                        }`}></div>
                        <span>{project.status}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-text-muted">Progress</span>
                        <span className="font-medium text-text-primary">{project.completion}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary rounded-full h-2 transition-all duration-500"
                          style={{ width: `${project.completion}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2 text-text-muted">
                        <Icon name="Calendar" size={16} />
                        <span>Deadline</span>
                      </div>
                      <span className="font-medium text-text-primary">{project.deadline}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Availability Calendar */}
        <div className="mt-12">
          <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-text-primary mb-2">Upcoming Availability</h3>
              <p className="text-text-secondary">Plan your project timeline with my availability schedule</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {availabilityCalendar.map((period, index) => (
                <div key={index} className="text-center">
                  <div className={`rounded-xl p-6 border-2 transition-colors ${
                    period.status === 'Available' ?'border-success/20 bg-success/5' :'border-warning/20 bg-warning/5'
                  }`}>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      period.status === 'Available' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      <Icon 
                        name={period.status === 'Available' ? 'CheckCircle' : 'Clock'} 
                        size={24} 
                      />
                    </div>
                    <h4 className="font-semibold text-text-primary mb-2">{period.month}</h4>
                    <p className={`text-sm font-medium mb-2 ${
                      period.status === 'Available' ? 'text-success' : 'text-warning'
                    }`}>
                      {period.status}
                    </p>
                    <p className="text-xs text-text-muted">
                      {period.slots} project slots
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <div className="inline-flex items-center space-x-4 bg-surface px-6 py-3 rounded-lg">
                <Icon name="Info" size={20} className="text-accent" />
                <p className="text-sm text-text-secondary">
                  Availability updates in real-time. Book early to secure your preferred timeline.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AvailabilityStatus;